// LIVE-TEST-STUDIO-CONSOLE-PARITY-1 · TEST 공유 세션 (모바일 Studio · PC Studio · PC 보조 콘솔 공통).
// 상품 계산은 이 파일의 순수 함수로 통일하고, 서버(/seller/test-live/state)는 버전과 함께 보관만 한다.
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from './api';
import { applyTimerLocal } from './timerLocal';

export const TEST_SID = '__TEST__';
export const SHARED_KEYS = ['live_products', 'active_product', 'active_product_display', 'active_product_qr', 'product_visibility_mode',
  'timer', 'timer_presets', 'external_notices', 'external_notice_selected', 'live_notice', 'past_slide'];
// STUDIO-TIMER-EXTERNAL-NOTICE-PRESET-1 · 타이머 문구·외부송출 공지 이미지 각 최대 5개.
export const PRESET_MAX = 5;
const POLL_MS = 2000;

// SELF-NOTICE-SYNC-UI-FIX-1 · live_notice 는 서버와 같은 형태(text·updated_at)로 비교·전송 → 불필요한 재푸시 방지.
const _sharedNotice = (n) => (n?.text ? { text: String(n.text).trim(), ...(n.notice_id ? { notice_id: String(n.notice_id).slice(0, 64) } : {}), updated_at: String(n.updated_at || '').slice(0, 40) } : null);
export const pickShared = (ctx) => SHARED_KEYS.reduce((o, k) => ({ ...o, [k]: k === 'live_notice' ? _sharedNotice(ctx?.[k]) : (ctx?.[k] ?? null) }), {});

// 테스트 샘플 상품 10개 (운영 DB 와 무관 · product_id 'test_product_' 접두사).
const _NAMES = ['스트라이프 니트', '데님 팬츠', '여성 재킷', '후드 티셔츠', '스니커즈',
  '트렌치 코트', '플리츠 스커트', '카디건', '라운드 티', '크로스백'];
const _PRICES = [39000, 45000, 59000, 35000, 69000, 129000, 42000, 48000, 25000, 55000];
export const buildTestLiveProducts = () => _NAMES.map((name, i) => ({
  product_id: `test_product_${i + 1}`,
  slot_number: i + 1,
  name: `${i + 1}번 · ${name}`,
  sale_price: _PRICES[i] || 30000,
  image: `https://picsum.photos/seed/livemoa_test_${i + 1}/300/300`,
  sale_status: '판매 중',
  is_introduced: false,
  available_stock: 20 - i,
  ...(i < 3 ? { size_table: {
    columns: ['사이즈', '총장', '가슴'],
    rows: [['S', '62', '48'], ['M', '64', '51'], ['L', '66', '54']],
    guide_text: '테스트 샘플 상품설명 · 실제 데이터 아님',
  } } : {}),
  ...(i === 3 ? { size_table: { columns: [], rows: [], guide_text: '', material: '면 100%', etc: '신축성 좋음 / 비침 없음' } } : {}),
  ...(i === 4 ? { size_table: { columns: ['사이즈'], rows: [['M'], ['L'], ['XL']], guide_text: '',
    material: '면 80%, 폴리 20%', etc: '신축성 좋음 / 비침 없음 / 드라이클리닝 권장' } } : {}),
  ...(i === 5 ? { size_table: { columns: ['사이즈', '총장', '어깨', '가슴', '소매'],
    rows: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'].map((s, k) => [s, `${100 + k * 2}`, `${40 + k}`, `${50 + k * 2}`, `${58 + k}`]),
    guide_text: '' } } : {}),
  stock_status: 'tracked',
}));
export const buildTestShared = () => ({
  live_products: buildTestLiveProducts(), active_product: null, active_product_display: null,
  active_product_qr: null, product_visibility_mode: 'all',
  timer: null, timer_presets: [], external_notices: [], external_notice_selected: null,
});

// 소개 전환 (Studio ProductsTab · 리모컨 · 콘솔 공통).
export const applyTestIntroduce = (c, pid, draftPreset) => {
  const prod = (c?.live_products || []).find((p) => p.product_id === pid);
  if (!prod) return c;
  return {
    ...c,
    active_product: prod,
    active_product_display: { preset: draftPreset || 'default', position: 'bottom', mode: 'visible' },
    live_products: (c.live_products || []).map((p) => ({
      ...p,
      is_introduced: p.product_id === pid ? true : p.is_introduced,
    })),
  };
};

const _sellable = (p) => (p.sale_status || '판매 중') !== '품절';
const _renumber = (list) => list.map((p, i) => ({ ...p, slot_number: i + 1 }));

// PC 보조 콘솔 조작 → 공유 상태 변경 (실제 방송 API 호출 없음).
export const testOps = {
  announce: (c, pid) => applyTestIntroduce(c, pid),
  clear: (c) => ({ ...c, active_product: null, active_product_display: null, active_product_qr: null }),
  hideOverlay: (c) => ({ ...c, active_product_display: { ...(c.active_product_display || {}), mode: 'hidden' } }),
  unintroduce: (c, pid) => ({
    ...c,
    live_products: (c.live_products || []).map((p) => (p.product_id === pid ? { ...p, is_introduced: false } : p)),
    active_product: c?.active_product?.product_id === pid ? null : c?.active_product,
  }),
  resetIntroduced: (c) => ({
    ...c, active_product: null, active_product_display: null,
    live_products: (c.live_products || []).map((p) => ({ ...p, is_introduced: false })),
  }),
  visibility: (c, mode) => ({ ...c, product_visibility_mode: mode }),
  soldout: (c, pid) => {
    const list = (c.live_products || []).map((p) => (p.product_id === pid ? { ...p, sale_status: '품절' } : p));
    const next = { ...c, live_products: list };
    if (c?.active_product?.product_id !== pid) return next;
    const idx = list.findIndex((p) => p.product_id === pid);
    const cand = list.slice(idx + 1).find(_sellable);
    return cand ? applyTestIntroduce(next, cand.product_id) : testOps.clear(next);
  },
  remove: (c, pid) => {
    const next = { ...c, live_products: _renumber((c.live_products || []).filter((p) => p.product_id !== pid)) };
    return c?.active_product?.product_id === pid ? testOps.clear(next) : next;
  },
  reorder: (c, ids) => {
    const by = Object.fromEntries((c.live_products || []).map((p) => [p.product_id, p]));
    return { ...c, live_products: _renumber(ids.map((id) => by[id]).filter(Boolean)) };
  },
  setSlot: (c, pid, slot) => {
    const list = (c.live_products || []).filter((p) => p.product_id !== pid);
    const me = (c.live_products || []).find((p) => p.product_id === pid);
    if (!me) return c;
    list.splice(Math.min(Math.max(slot - 1, 0), list.length), 0, me);
    return { ...c, live_products: _renumber(list) };
  },
  // 빠른등록 → 목록 끝에 추가 · announce_now 가 아니면 현재 소개 상품 유지.
  quickAdd: (c, body) => {
    const list = c?.live_products || [];
    const prod = {
      product_id: `test_product_quick_${Date.now()}`,
      slot_number: list.length + 1,
      name: body.name,
      sale_price: body.sale_price,
      image: body.image || null,
      sale_status: '판매 중',
      is_introduced: !!body.announce_now,
      available_stock: body.stock != null ? body.stock : 999,
      stock_status: body.stock != null ? 'tracked' : 'infinite',
    };
    const next = { ...(c || {}), live_products: [...list, prod] };
    return { ctx: body.announce_now ? applyTestIntroduce(next, prod.product_id) : next, product: prod };
  },
};

// 공유 상태 → PC 보조 콘솔 data 형식 (/live-products 응답과 같은 필드만 사용).
export const toConsoleData = (c) => (c ? {
  items: (c.live_products || []).map((p, i) => ({ ...p, sort_order: i, slot_number: p.slot_number || i + 1 })),
  active_product_id: c.active_product?.product_id || null,
  product_visibility_mode: c.product_visibility_mode || 'all',
  streaming_session_id: null,
  live_notice: c.live_notice || null,
  timer: c.timer || null,
  timer_presets: c.timer_presets || [],
  external_notices: c.external_notices || [],
  external_notice_selected: c.external_notice_selected || null,
  active_product_display: c.active_product_display || null,
  active_product_qr: c.active_product_qr || null,
  past_slide: c.past_slide || null,
  __test: true,
} : null);

// 지난 상품 슬라이드 대상: 소개했던 상품(현재 소개 제외) · 소개 시각(없으면 순번) 순.
export const pastListOf = (ctx) => {
  const activeId = ctx?.active_product?.product_id;
  const key = (p) => (p.introduced_at ? new Date(p.introduced_at).getTime() : (p.slot_number || 0));
  return (ctx?.live_products || []).filter((p) => p.is_introduced && p.product_id !== activeId).sort((a, b) => key(a) - key(b));
};

// 기존 이미지 항목(type 없음)은 image 로 취급 (하위 호환).
export const noticeType = (n) => n?.type || (n?.url ? 'image' : 'text');
const _pid = (p) => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
// STUDIO-TIMER-EXTERNAL-NOTICE-PRESET-1 · 타이머 문구·외부 공지 이미지 조작 (Studio·콘솔 공용 순수 함수).
export const presetOps = {
  timer: (c, action, extra) => ({ ...c, timer: applyTimerLocal(c?.timer, action, extra) }),
  addTimerPreset: (c, label, minutes) => ((c.timer_presets || []).length >= PRESET_MAX ? c
    : { ...c, timer_presets: [...(c.timer_presets || []), { id: _pid('tp'), label, minutes }] }),
  updateTimerPreset: (c, id, patch) => ({ ...c, timer_presets: (c.timer_presets || []).map((p) => (p.id === id ? { ...p, ...patch } : p)) }),
  removeTimerPreset: (c, id) => ({ ...c, timer_presets: (c.timer_presets || []).filter((p) => p.id !== id) }),
  addNotice: (c, url) => ((c.external_notices || []).length >= PRESET_MAX ? c
    : { ...c, external_notices: [...(c.external_notices || []), { id: _pid('en'), type: 'image', url }] }),
  // STUDIO-NOTICE-EXTERNAL-UI-REORGANIZE-1 · 텍스트 공지 (이미지와 합산 최대 5개).
  addTextNotice: (c, text) => ((c.external_notices || []).length >= PRESET_MAX ? c
    : { ...c, external_notices: [...(c.external_notices || []), { id: _pid('en'), type: 'text', text }] }),
  updateTextNotice: (c, id, text) => ({ ...c, external_notices: (c.external_notices || []).map((n) => (n.id === id ? { ...n, text } : n)) }),
  replaceNotice: (c, id, url) => ({ ...c, external_notices: (c.external_notices || []).map((n) => (n.id === id ? { ...n, url } : n)) }),
  removeNotice: (c, id) => ({
    ...c, external_notices: (c.external_notices || []).filter((n) => n.id !== id),
    external_notice_selected: c.external_notice_selected === id ? null : c.external_notice_selected,
  }),
  selectNotice: (c, id) => ({ ...c, external_notice_selected: c.external_notice_selected === id ? null : id }),
};

// LIVE-TEST-CONCURRENT-CONTROL-SAFETY-1 · 3방향 병합 (base=마지막 서버 합의, mine=이 기기, theirs=서버 최신).
// 서로 다른 상품·필드 변경은 모두 유지. 같은 상품·같은 필드를 서로 다르게 바꾼 경우 서버(먼저 수락된 쪽) 우선 + 충돌 보고.
// 소개(active_*)·공개 범위는 '마지막 명령' 이므로 이 기기가 바꾨다면 이 기기 값을 서버가 다음에 수락.
const _stable = (v) => {
  if (Array.isArray(v)) return `[${v.map(_stable).join(',')}]`;
  if (v && typeof v === 'object') return `{${Object.keys(v).sort().map((k) => `${k}:${_stable(v[k])}`).join(',')}}`;
  return JSON.stringify(v ?? null);
};
const _eq = (a, b) => _stable(a) === _stable(b);
const SCALARS = ['active_product', 'active_product_display', 'active_product_qr', 'product_visibility_mode',
  'timer', 'external_notice_selected', 'live_notice', 'past_slide'];
const LISTS = ['timer_presets', 'external_notices'];
const _byId = (list) => Object.fromEntries((list || []).map((p) => [p.product_id, p]));

const _mergeProduct = (b, m, t, conflicts) => {
  if (!b) return m && t ? { ...m, ...t } : (m || t);
  if (!m || !t) {
    // 한쪽 삭제: 다른 쪽이 수정했어도 삭제 우선(서버 삭제) · 이 기기 삭제는 서버 미수정일 때만.
    if (!t) return null;
    if (_eq(t, b)) return null;
    conflicts.push({ product_id: b.product_id, field: '(삭제)' });
    return t;
  }
  const out = {};
  new Set([...Object.keys(b), ...Object.keys(m), ...Object.keys(t)]).forEach((k) => {
    if (k === 'slot_number') return;
    const mv = m[k]; const tv = t[k]; const bv = b[k];
    if (_eq(mv, bv)) { if (tv !== undefined) out[k] = tv; return; }
    if (_eq(tv, bv) || _eq(mv, tv)) { if (mv !== undefined) out[k] = mv; return; }
    conflicts.push({ product_id: b.product_id, field: k });
    if (tv !== undefined) out[k] = tv;
  });
  return out;
};

const _mergeOrder = (b, m, t) => {
  const ids = (l) => (l || []).map((p) => p.product_id);
  const common = (l, keep) => l.filter((id) => keep.has(id));
  const bIds = ids(b); const mIds = ids(m); const tIds = ids(t);
  const bSet = new Set(bIds);
  const mineMoved = !_eq(common(mIds, bSet), common(bIds, new Set(mIds)));
  const theirsMoved = !_eq(common(tIds, bSet), common(bIds, new Set(tIds)));
  const primary = mineMoved && !theirsMoved ? mIds : tIds;
  const secondary = primary === mIds ? tIds : mIds;
  const out = [...primary];
  secondary.forEach((id) => { if (!out.includes(id)) out.push(id); });
  return out;
};

export const normalizeShared = (s) => {
  const list = (s.live_products || []).map((p, i) => ({ ...p, slot_number: i + 1 }));
  const ap = s.active_product && list.find((p) => p.product_id === s.active_product.product_id);
  const notices = (s.external_notices || []).slice(0, PRESET_MAX);
  const sel = notices.some((n) => n.id === s.external_notice_selected) ? s.external_notice_selected : null;
  return { ...s, live_products: list, active_product: s.active_product ? (ap || null) : null,
    timer_presets: (s.timer_presets || []).slice(0, PRESET_MAX), external_notices: notices, external_notice_selected: sel };
};

// id 키 목록(타이머 문구·공지 이미지) — 상품과 같은 항목 단위 3방향 규칙 재사용.
const _asP = (l) => (l || []).map((x) => ({ ...x, product_id: x.id }));
const _mergeList = (b, m, t, conflicts) => {
  const B = _byId(_asP(b)); const M = _byId(_asP(m)); const T = _byId(_asP(t));
  const merged = {};
  new Set([...Object.keys(B), ...Object.keys(M), ...Object.keys(T)]).forEach((id) => {
    const p = _mergeProduct(B[id], M[id], T[id], conflicts);
    if (p) merged[id] = p;
  });
  return _mergeOrder(_asP(b), _asP(m), _asP(t)).filter((id) => merged[id])
    .map((id) => { const { product_id, ...x } = merged[id]; return x; }); // eslint-disable-line no-unused-vars
};

export const merge3 = (base, mine, theirs) => {
  const conflicts = [];
  if (!base) return { state: normalizeShared(theirs), conflicts };
  const B = _byId(base.live_products); const M = _byId(mine.live_products); const T = _byId(theirs.live_products);
  const merged = {};
  new Set([...Object.keys(B), ...Object.keys(M), ...Object.keys(T)]).forEach((id) => {
    const p = _mergeProduct(B[id], M[id], T[id], conflicts);
    if (p) merged[id] = p;
  });
  const order = _mergeOrder(base.live_products, mine.live_products, theirs.live_products).filter((id) => merged[id]);
  const out = { live_products: order.map((id) => merged[id]) };
  LISTS.forEach((k) => {
    let list = _mergeList(base[k], mine[k], theirs[k], conflicts);
    if (list.length > PRESET_MAX) {
      // 양쪽 동시 추가로 한도 초과 → 서버(먼저 수락) 항목 우선, 이 기기 추가분은 거절 + 안내.
      const keep = new Set((theirs[k] || []).map((x) => x.id));
      const rest = list.filter((x) => !keep.has(x.id));
      list = [...list.filter((x) => keep.has(x.id)), ...rest].slice(0, PRESET_MAX);
      rest.slice(Math.max(0, PRESET_MAX - keep.size)).forEach((x) => conflicts.push({ product_id: x.id, field: '(최대 5개 초과)' }));
    }
    out[k] = list;
  });
  // 같은 항목 충돌로 이 기기 조작이 진 경우, 그 조작의 소개 이동(품절 후 자동 다음 등)도 적용하지 않음.
  SCALARS.forEach((k) => { out[k] = conflicts.length || _eq(mine[k], base[k]) ? theirs[k] : mine[k]; });
  return { state: normalizeShared(out), conflicts };
};

const _rid = () => `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

// 서버 공유 세션과 로컬 ctx 를 동기화 (2초 폴링 + 변경 시 버전 확인 저장 + 충돌 시 3방향 병합).
export const useTestLiveShare = ({ enabled, ctx, setCtx, seed }) => {
  const ver = useRef(null);
  const last = useRef(null);
  const base = useRef(null);
  const ctxRef = useRef(ctx);
  const inflight = useRef(false);
  const dirty = useRef(false);
  const [status, setStatus] = useState('idle');
  const [account, setAccount] = useState(null);
  ctxRef.current = ctx;

  const notify = (conflicts) => {
    if (!conflicts.length) return;
    toast.warning(`다른 기기에서 같은 항목을 먼저 바꿔 그 변경을 적용했어요 (${conflicts.length}건)`, { id: 'test-live-conflict' });
  };
  // 서버 최신 상태 반영. 아직 올리지 못한 로컬 변경이 있으면 버리지 않고 병합 후 다시 저장.
  const apply = (srv) => {
    const local = ctxRef.current?.live_products ? pickShared(ctxRef.current) : null;
    const pending = local && base.current && JSON.stringify(local) !== last.current;
    const { state, conflicts } = pending ? merge3(base.current, local, srv.state) : { state: srv.state, conflicts: [] };
    ver.current = srv.version;
    base.current = srv.state;
    last.current = JSON.stringify(srv.state);
    notify(conflicts);
    // 병합 결과를 즉시 ref 에 반영 → 재전송(push)이 렌더 전 옛 로컬 상태로 다른 기기 변경을 덮지 않도록.
    if (pending) ctxRef.current = { ...(ctxRef.current || {}), ...state };
    setCtx((c) => ({ ...(c || seed()), ...state }));
    if (pending) dirty.current = true;
  };
  const push = async () => {
    if (inflight.current) { dirty.current = true; return; }
    const cur = ctxRef.current;
    if (!cur?.live_products || ver.current == null) return;
    const body = pickShared(cur);
    const json = JSON.stringify(body);
    if (json === last.current) return;
    inflight.current = true;
    const sentVer = ver.current;
    try {
      const { data } = await api.put('/seller/test-live/state', { state: body, base_version: sentVer, request_id: _rid() });
      if (data.version > (ver.current || 0) || ver.current === sentVer) {
        ver.current = data.version;
        base.current = data.state;
        last.current = JSON.stringify(data.state);
      }
      setStatus('synced');
    } catch (e) {
      const cur409 = e?.response?.status === 409 && e.response.data?.detail?.current;
      if (cur409 && cur409.state) apply(cur409); else setStatus('error');
    } finally {
      inflight.current = false;
      if (dirty.current) { dirty.current = false; setTimeout(push, 0); }
    }
  };

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const tick = async () => {
      if (inflight.current) return;
      try {
        const { data } = await api.get('/seller/test-live/state');
        if (alive && data.seller_id) setAccount((a) => (a?.id === data.seller_id ? a : { id: data.seller_id, name: data.seller_name }));
        if (!alive || inflight.current) return;
        if (!data.state) {
          // 서버 초기화(reset) 후에는 이전 로컬 상태를 되살리지 않고 기본 10개로 다시 시작.
          const wasShared = (ver.current || 0) > 0;
          ver.current = 0;
          base.current = null;
          setCtx((c) => (wasShared ? { ...(c || seed()), ...pickShared(seed()) } : (c?.live_products ? c : seed())));
          last.current = null;
          setTimeout(push, 0);
        } else if (data.version > (ver.current || 0) || !ctxRef.current?.live_products) {
          // 이전(더 낮은 버전) 응답은 무시 → 최신 화면을 되돌리지 않음.
          apply(data);
          if (dirty.current) { dirty.current = false; setTimeout(push, 0); }
        }
        setStatus((s) => (s === 'error' ? 'synced' : s === 'idle' ? 'synced' : s));
      } catch { if (alive) setStatus('error'); }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const sharedJson = enabled && ctx?.live_products ? JSON.stringify(pickShared(ctx)) : null;
  useEffect(() => {
    if (sharedJson && sharedJson !== last.current) push();
  }, [sharedJson]); // eslint-disable-line react-hooks/exhaustive-deps

  return { status, account };
};

export const resetTestLive = () => api.post('/seller/test-live/reset');
